import downloadIcon from 'images/icons/svg/download.svg';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { toast } from 'react-toastify';
import AuthService from 'services/AuthService';
import { FileModel, LanguageModel, UploadDocumentTypesModel } from 'services/EpdClient';
import FileService from 'services/FileService';
import styled from 'styled-components';
import { defaultThemeColors } from 'styles/theme';
import { Option } from 'types/types';
import TextWithAnimatedEllipsis from 'components/v2/file-upload/TextWithAnimatedEllipsis';
import Tooltip from 'components/controls/Tooltip';

interface Props {
  accept: string;
  title: string;
  uploadFile(e: React.ChangeEvent<HTMLInputElement>, field: string, selectedDocType?: string, selectedLang?: string): void;
  disabled: boolean;
  uploadDisabled?: boolean;
  documents: FileModel[] | undefined;
  onRemoveFile(fileId: string): void;
  field: string;
  languageList?: LanguageModel[];
  documentTypeList?: UploadDocumentTypesModel[];
  validExtensions?: string[];
  singleFile?: boolean;
  maxSize?: boolean;
  showUploadingIndicator?: boolean;
  footerNode?: React.ReactNode;
}

const UploadFile: React.FC<Props> = (props) => {
  const [selectedDocType, setSelectedDocType] = useState<string | undefined>();
  const [selectedLang, setSelectedLang] = useState<string | undefined>();

  const { t } = useTranslation();
  const inputRef = React.createRef();

  const withAdditionalProperties = props.languageList !== undefined && props.documentTypeList !== undefined;

  const selectFile = () => {
    if (withAdditionalProperties && (isEmpty(selectedLang) || isEmpty(selectedDocType))) {
      toast.error(t('messages.uploadFileLangNeeded') as string, { position: 'top-center' });
      return;
    }

    if (props.singleFile && props.documents && props.documents?.length > 0) {
      toast.error(t('messages.uploadFileSingle') as string, { position: 'top-center' });
      return;
    }

    (inputRef as any).current.click();
  };

  const upload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const validExtensions = props.validExtensions;
    if (validExtensions && validExtensions?.length > 0 && e.target.files) {
      for (const file of e.target.files) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (!fileExtension || !validExtensions.includes(fileExtension)) {
          toast.error(`Please upload a file with the allowed extension(s): ${validExtensions.join('/')}.`, {
            position: 'top-center',
          });
          return false;
        }
      }
    }
    if (withAdditionalProperties && (!selectedDocType || !selectedLang)) {
      return;
    }
    if (withAdditionalProperties) {
      props.uploadFile(e, props.field, selectedDocType, selectedLang);
      setSelectedLang(undefined);
      setSelectedDocType(undefined);
      return;
    }
    props.uploadFile(e, props.field);
  };

  const renderUploadLabel = () => {
    const additionalPropertiesNotEmpty = !!selectedDocType && !!selectedLang;
    if (props.disabled) return null;
    if (withAdditionalProperties) {
      return (
        <UploadLabel
          style={{
            cursor: 'pointer',
            background: additionalPropertiesNotEmpty ? defaultThemeColors.orange : defaultThemeColors.elementsButtonDisabled,
            pointerEvents: additionalPropertiesNotEmpty ? 'auto' : 'none',
          }}
          onClick={() => selectFile()}
        >
          {t('upload')}
        </UploadLabel>
      );
    }
    return (
      <UploadLabel style={{ cursor: 'pointer' }} onClick={() => selectFile()}>
        {t('upload')}
      </UploadLabel>
    );
  };

  const isEpdDocumentAttached = props?.documents?.some((x: any) => x.name.includes('EPD document'));

  const DocumentTypeSelectStyle = {
    menu: (styles: any) => ({ ...styles, zIndex: 999 }),
    control: (styles: any) => ({
      ...styles,
      borderRadius: 0,
      borderColor: isEpdDocumentAttached ? 'gray' : 'red',
      color: 'black',
      fontSize: '12px',
      minHeight: '30px',
      minWidth: '20px',
      width: '100%',
      background: 'white',
    }),
    multiValueLabel: (styles: any) => ({
      ...styles,
      fontSize: '100%',
    }),
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <FileBox>
        {props.maxSize ? (
          <h3 style={{ fontSize: '14px' }}> {props.title} (max&nbsp;10&nbsp;MB)</h3>
        ) : (
          <h3 style={{ fontSize: '14px' }}> {props.title}</h3>
        )}
        <div>
          <input
            ref={inputRef as any}
            type="file"
            accept={props.accept}
            value={''}
            onChange={(e) => upload(e)}
            disabled={props.disabled}
            style={{ visibility: 'hidden', display: 'none' }}
            title=""
          />

          {!isEmpty(props.documents) && (
            <FileContainer>
              {props?.documents?.map((file: any) => (
                <FileRow key={file.id} file={file} onRemoveFile={props.onRemoveFile} disabled={props.disabled} />
              ))}
            </FileContainer>
          )}

          {withAdditionalProperties && !props.disabled && (
            <FileContainer>
              <MaterialGrid>
                <MaterialColumn1>
                  Document type
                  <Select
                    options={props?.documentTypeList?.map((x: UploadDocumentTypesModel) => {
                      return { value: x.id, label: x.name } as Option;
                    })}
                    styles={DocumentTypeSelectStyle}
                    value={
                      props?.documentTypeList?.find((x) => x.id === selectedDocType)
                        ? {
                            value: selectedDocType,
                            label: props.documentTypeList.find((x) => x.id === selectedDocType)?.name,
                          }
                        : null
                    }
                    isClearable
                    onChange={(e) => setSelectedDocType((e as Option)?.value)}
                  />
                </MaterialColumn1>
                <MaterialColumn2>
                  Language
                  <Select
                    options={props?.languageList?.map((x: LanguageModel) => {
                      return { value: x.id, label: x.name } as Option;
                    })}
                    styles={SelectStyle}
                    isClearable
                    value={
                      props?.languageList?.find((x) => x.id === selectedLang)
                        ? {
                            value: selectedLang,
                            label: props.languageList.find((x) => x.id === selectedLang)?.name,
                          }
                        : null
                    }
                    onChange={(e) => setSelectedLang((e as Option)?.value)}
                  />
                </MaterialColumn2>
              </MaterialGrid>
              {!isEpdDocumentAttached && (
                <div style={{ color: 'red', marginTop: 10 }}>EPD Document type file is required</div>
              )}
            </FileContainer>
          )}
          {!props.uploadDisabled && renderUploadLabel()}

          {props.showUploadingIndicator && <UploadingText>Uploading file</UploadingText>}
          {props.footerNode}
        </div>
      </FileBox>
    </div>
  );
};

export const FileRow: React.FunctionComponent<{
  file?: FileModel;
  onRemoveFile(fileId: string): void;
  disabled: boolean;
}> = ({ file, onRemoveFile, disabled }) => {
  const { t } = useTranslation();
  if (!file?.id) {
    return null;
  }

  const fileUrl = FileService.getImageUrl(file?.id || '');

  return (
    <FileBoxRow>
      {file && (
        <div>
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            <img alt="Download file" src={downloadIcon} style={{ width: '20px', margin: '0 0.5rem', cursor: 'pointer' }} />
            <Tooltip position="title">Download file</Tooltip>
          </a>
        </div>
      )}
      <div style={{ margin: '0.5rem 0.5rem 0.5rem 0' }}>
        <Text>{file?.name} </Text>
      </div>

      {!disabled && file.isRemovable && (
        <RemoveBox>
          <button onClick={() => onRemoveFile(file.id!)}>{t('remove')}</button>
        </RemoveBox>
      )}
    </FileBoxRow>
  );
};

export default UploadFile;

const FileContainer = styled.div`
  margin-bottom: 1rem;
`;

const UploadLabel = styled.label`
  font-size: 0.8rem;
  padding: 0.5rem;
  margin: 0.5rem 0;
  background: ${(props) => props.theme.colors.orange};
  border-radius: 5px;
  color: white;

  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none;

  :hover {
    background: ${(props) => props.theme.colors.lightOrange};
  }
`;

export const FileBox = styled.div`
  background-color: ${(props) => props.theme.colors.regionColorLight};
  padding: 1rem 3rem;
  flex: 1 0 auto;

  > h3 {
    margin-top: 0;
  }
`;

const FileBoxRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: white;
  font-size: 14px;
`;

const Text = styled.span`
  max-width: fit-content;
  word-break: break-word;
`;

const RemoveBox = styled.div`
  margin-left: auto;
  padding-right: 0.5rem;

  > button {
    cursor: pointer;
    font-size: 0.8rem;
    padding: 0.3rem;
    margin: 0.5rem 0;
    background: rgb(105, 105, 105);
    border-radius: 5px;
    color: white;
    :hover {
      background: rgb(128, 128, 128);
    }
  }
`;

const Column = styled.div`
  align-self: center;
  width: 100%;
`;

const MaterialGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 1rem;
  grid-row-gap: 0.5rem;
  width: 100%;
`;

const MaterialColumn1 = styled(Column)`
  grid-column-start: 1;
  grid-column-end: 2;
`;

const MaterialColumn2 = styled(Column)`
  grid-column-start: 2;
  grid-column-end: 3;
`;

const UploadingText = styled(TextWithAnimatedEllipsis)`
  color: inherit;
  margin-top: 16px;
`;

export const SelectStyle = {
  menu: (styles: any) => ({ ...styles, zIndex: 999 }),
  control: (styles: any) => ({
    ...styles,
    borderRadius: 0,
    borderColor: 'gray',
    color: 'black',
    fontSize: '12px',
    minHeight: '30px',
    minWidth: '20px',
    width: '100%',
    background: 'white',
  }),
  multiValueLabel: (styles: any) => ({
    ...styles,
    fontSize: '100%',
  }),
};
